import {DocumentVersion} from 'notebook/documents/DocumentConstants';
import {EntryProtocol, EntryVersion, ProtocolVersion} from 'notebook/ELNConstants';
import {SubtemplateVersion, TemplateVersion} from 'notebook/entry-templates/TemplateConstants';
import {SimpleTableDoc} from 'notebook/simple-tables/useSimpleEvaluation';
import {BioentityNote, EntryNote} from 'notebook/util/NoteUtilConstants';

/**
 * The two "base documents" are Note and ProtocolVersion, each of which have a component for editing their
 * contents and have a spreadsheet evaluation context.
 *
 * There are "higher-order" documents that are hierarchical and ultimate have leaves of the "base documents"
 * (i.e. TemplateVersion -> EntryVersion -> Note / EntryProtocol -> ProtocolVersion)
 *
 * This module understands the composition of these primitive building blocks and allows one to write a
 * traverser (DocumentTraverseVisitor) or mapper (DocumentMapVisitor) over a higher-order document
 * that targets manipulating the base documents.
 *
 * Besides the base documents, we also support SimpleTableDoc, an in-memory document that can be used
 * in conjunction with SimpleTableEditable.
 */
export interface DocumentVisitor<TP, TE, TN, TS> {
  visitProtocolVersion(item: ProtocolVersion, oldItem: ProtocolVersion | null): TP;
  visitNote(item: EntryNote, oldItem: EntryNote | null): TE;
  visitNote(item: BioentityNote, oldItem: BioentityNote | null): TN;
  visitSimpleTableDoc(item: SimpleTableDoc, oldItem: SimpleTableDoc | null): TS;
}

/* These know how to manipulate any core document type (note / protocol) */
export type DocumentMapVisitor = DocumentVisitor<ProtocolVersion, EntryNote, BioentityNote, SimpleTableDoc>;
export type DocumentTraverseVisitor = DocumentVisitor<void, void, void, void>;

/* These know how to actually traverse any versioned hierarchy */
export class DocumentMapper {
  visitor: DocumentMapVisitor;

  constructor(visitor: DocumentMapVisitor) {
    this.visitor = visitor;
  }

  mapDocumentVersion(
    documentVersion: DocumentVersion,
    oldDocumentVersion: DocumentVersion | null
  ): DocumentVersion {
    return {
      ...documentVersion,
      entryVersion: this.mapEntryVersion(
        documentVersion.entryVersion,
        oldDocumentVersion?.entryVersion || null
      ),
    };
  }

  mapTemplateVersion(
    templateVersion: TemplateVersion,
    oldTemplateVersion: TemplateVersion | null
  ): TemplateVersion {
    return {
      ...templateVersion,
      entryVersion: this.mapEntryVersion(
        templateVersion.entryVersion,
        oldTemplateVersion?.entryVersion || null
      ),
    };
  }

  mapSubtemplateVersion(
    subtemplateVersion: SubtemplateVersion,
    oldSubtemplateVersion: SubtemplateVersion | null
  ): SubtemplateVersion {
    return {
      ...subtemplateVersion,
      noteVersion: this.mapBioentityNote(
        subtemplateVersion.noteVersion,
        oldSubtemplateVersion?.noteVersion || null
      ),
    };
  }

  mapEntryVersion(entryVersion: EntryVersion, oldEntryVersion: EntryVersion | null): EntryVersion {
    return {
      ...entryVersion,
      note: this.mapEntryNote(entryVersion.note, oldEntryVersion?.note || null),
      entryProtocols: entryVersion.entryProtocols.map((ep: EntryProtocol) => {
        const oldEp =
          oldEntryVersion?.entryProtocols?.find((anOldEp: EntryProtocol) => anOldEp.cid === ep.cid) || null;
        return this.mapEntryProtocol(ep, oldEp);
      }),
    };
  }

  mapEntryProtocol(entryProtocol: EntryProtocol, oldEntryProtocol: EntryProtocol | null): EntryProtocol {
    if (entryProtocol.externalFile) {
      return entryProtocol;
    }
    return {
      ...entryProtocol,
      ...this.mapProtocolVersion(entryProtocol, oldEntryProtocol?.externalFile ? null : oldEntryProtocol),
    };
  }

  mapProtocolVersion(
    protocolVersion: ProtocolVersion,
    oldProtocolVersion: ProtocolVersion | null
  ): ProtocolVersion {
    return this.visitor.visitProtocolVersion(protocolVersion, oldProtocolVersion);
  }

  mapEntryNote(note: EntryNote, oldNote: EntryNote | null): EntryNote {
    return this.visitor.visitNote(note, oldNote);
  }

  mapBioentityNote(note: BioentityNote, oldNote: BioentityNote | null): BioentityNote {
    return this.visitor.visitNote(note, oldNote);
  }

  mapSimpleTableDoc(doc: SimpleTableDoc, oldDoc: SimpleTableDoc | null): SimpleTableDoc {
    return this.visitor.visitSimpleTableDoc(doc, oldDoc);
  }
}

export class DocumentTraverser {
  visitor: DocumentTraverseVisitor;

  constructor(visitor: DocumentTraverseVisitor) {
    this.visitor = visitor;
  }

  walkDocumentVersion(documentVersion: DocumentVersion, oldDocumentVersion: DocumentVersion | null): void {
    this.walkEntryVersion(documentVersion.entryVersion, oldDocumentVersion?.entryVersion || null);
  }

  walkTemplateVersion(templateVersion: TemplateVersion, oldTemplateVersion: TemplateVersion | null): void {
    this.walkEntryVersion(templateVersion.entryVersion, oldTemplateVersion?.entryVersion || null);
  }

  walkEntryVersion(entryVersion: EntryVersion, oldEntryVersion: EntryVersion | null): void {
    this.walkEntryNote(entryVersion.note, oldEntryVersion?.note || null);
    entryVersion.entryProtocols.forEach((ep: EntryProtocol) => {
      if (ep.externalFile) {
        return;
      }
      const oldEp =
        oldEntryVersion?.entryProtocols?.find((anOldEp: EntryProtocol) => anOldEp.cid === ep.cid) || null;
      this.walkProtocolVersion(ep, oldEp?.externalFile ? null : oldEp);
    });
  }

  walkProtocolVersion(protocolVersion: ProtocolVersion, oldProtocolVersion: ProtocolVersion | null): void {
    this.visitor.visitProtocolVersion(protocolVersion, oldProtocolVersion);
  }

  walkEntryNote(note: EntryNote, oldNote: EntryNote | null): void {
    this.visitor.visitNote(note, oldNote);
  }

  walkSubtemplateVersion(
    subtemplateVersion: SubtemplateVersion,
    oldSubtemplateVersion: SubtemplateVersion | null
  ): void {
    this.walkBioentityNote(subtemplateVersion.noteVersion, oldSubtemplateVersion?.noteVersion || null);
  }

  walkBioentityNote(note: BioentityNote, oldNote: BioentityNote | null): void {
    this.visitor.visitNote(note, oldNote);
  }

  walkSimpleTableDoc(doc: SimpleTableDoc, oldDoc: SimpleTableDoc | null): void {
    this.visitor.visitSimpleTableDoc(doc, oldDoc);
  }
}
